// components/learning/InlineMathField.jsx
import React, { useState, useRef, useEffect } from 'react';
import { MathfieldElement } from 'mathlive';
import { GripVertical, X } from 'lucide-react';

const getDefaultPosition = (anchorRect) => {
  if (anchorRect) {
    const x = Math.max(80, Math.min(window.innerWidth - 420, anchorRect.left + 40));
    const y = Math.max(80, Math.min(window.innerHeight - 320, anchorRect.top + 40));
    return { x, y };
  }

  return {
    x: Math.max(80, window.innerWidth / 2 - 200),
    y: Math.max(80, window.innerHeight / 2 - 140),
  };
};

export const InlineMathModal = ({
  isOpen,
  initialLatex = '',
  anchorRect = null,
  onInsert,
  onClose,
}) => {
  const [mathContent, setMathContent] = useState(initialLatex || '');
  const [editorPosition, setEditorPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const mathEditorRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!customElements.get('math-field')) {
      customElements.define('math-field', MathfieldElement);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setMathContent(initialLatex || '');
    setEditorPosition(getDefaultPosition(anchorRect));

    const timer = setTimeout(() => {
      mathEditorRef.current?.focus();
    }, 50);

    return () => clearTimeout(timer);
  }, [isOpen, initialLatex, anchorRect]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onInsert?.(mathContent);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, mathContent, onInsert]);

  const startDrag = (e) => {
    setIsDragging(true);
    setDragStartPos({
      x: e.clientX - editorPosition.x,
      y: e.clientY - editorPosition.y,
    });
    e.preventDefault();
  };

  const onDrag = (e) => {
    if (!isDragging) return;
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (clientX && clientY) {
      const width = modalRef.current?.offsetWidth || 400;
      const height = modalRef.current?.offsetHeight || 250;
      const newX = clientX - dragStartPos.x;
      const newY = clientY - dragStartPos.y;
      setEditorPosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - width)),
        y: Math.max(0, Math.min(newY, window.innerHeight - height)),
      });
    }
  };

  const stopDrag = () => setIsDragging(false);

  const startTouchDrag = (e) => {
    if (e.touches && e.touches[0]) {
      setIsDragging(true);
      setDragStartPos({
        x: e.touches[0].clientX - editorPosition.x,
        y: e.touches[0].clientY - editorPosition.y,
      });
    }
  };

  const onTouchDrag = (e) => {
    if (!isDragging || !e.touches || !e.touches[0]) return;

    const width = modalRef.current?.offsetWidth || 400;
    const height = modalRef.current?.offsetHeight || 250;
    const newX = e.touches[0].clientX - dragStartPos.x;
    const newY = e.touches[0].clientY - dragStartPos.y;

    setEditorPosition({
      x: Math.max(0, Math.min(newX, window.innerWidth - width)),
      y: Math.max(0, Math.min(newY, window.innerHeight - height)),
    });

    e.preventDefault();
  };

  const stopTouchDrag = () => setIsDragging(false);

  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', stopDrag);
    document.addEventListener('touchmove', onTouchDrag, { passive: false });
    document.addEventListener('touchend', stopTouchDrag);
    document.addEventListener('touchcancel', stopTouchDrag);

    return () => {
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', stopDrag);
      document.removeEventListener('touchmove', onTouchDrag);
      document.removeEventListener('touchend', stopTouchDrag);
      document.removeEventListener('touchcancel', stopTouchDrag);
    };
  }, [isDragging, dragStartPos, editorPosition]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 overflow-hidden">
      <div
        ref={modalRef}
        className="absolute bg-white rounded-lg shadow-lg"
        style={{
          left: `${editorPosition.x}px`,
          top: `${editorPosition.y}px`,
          width: '400px',
          maxWidth: '95vw',
        }}
      >
        <div className="flex items-center justify-between bg-gray-100 px-4 py-2 rounded-t-lg">
          <div
            onMouseDown={startDrag}
            onTouchStart={startTouchDrag}
            className="cursor-move bg-gray-200 hover:bg-gray-300 p-2 rounded mr-2 flex items-center justify-center"
            title="Drag to move"
          >
            <GripVertical size={16} className="mr-1" />
            <span className="text-xs">Drag</span>
          </div>
          <h3 className="ml-2 text-sm md:text-lg font-medium flex-grow">Math Editor</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none p-2"
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4">
          <div className="mb-2">
            <math-field
              ref={mathEditorRef}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#32289a] min-h-[60px]"
              style={{
                backgroundColor: '#fff',
                fontSize: '1.1em',
              }}
              value={mathContent}
              onChange={(e) => setMathContent(e.target.value)}
              virtual-keyboard-mode="onfocus"
              virtual-keyboards="all"
              smart-fence="true"
              smart-superscript="true"
            ></math-field>

            <div className="text-xs text-gray-500 mt-1">
              Press Ctrl+Enter to add a new line
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-[#32289a] text-white rounded-md hover:bg-opacity-90"
              onClick={() => onInsert?.(mathContent)}
            >
              Insert
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const InlineMathField = ({
  value,
  onChange,
  placeholder,
  rows = 3,
  textareaClassName = '',
  buttonLabel = 'Add math (Ctrl+M)',
  buttonClassName = '',
  buttonTitle = 'Insert mathematical expression (Ctrl+M)',
  showButton = true,
  disabled = false,
}) => {
  const textareaRef = useRef(null);
  const [showMathEditor, setShowMathEditor] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });
  const [initialLatex, setInitialLatex] = useState('');
  const [anchorRect, setAnchorRect] = useState(null);

  // Add Ctrl+M shortcut to text area
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'm') {
        if (disabled) return;
        e.preventDefault();
        openMathEditor();
      }
    };

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('keydown', handleKeyDown);
      return () => {
        textarea.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [value]);

  const openMathEditor = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    setCursorPosition({ start, end });

    if (selectedText) {
      if (selectedText.startsWith('$') && selectedText.endsWith('$')) {
        setInitialLatex(selectedText.slice(1, -1));
      } else {
        setInitialLatex(selectedText);
      }
    } else {
      setInitialLatex('');
    }

    setAnchorRect(textarea.getBoundingClientRect());
    setShowMathEditor(true);
  };

  const closeMathEditor = () => {
    setShowMathEditor(false);
    setInitialLatex('');
  };

  const handleInsertMath = (latex) => {
    if (!latex || !textareaRef.current) {
      closeMathEditor();
      return;
    }

    const { start, end } = cursorPosition;
    const newText =
      value.substring(0, start) +
      `$${latex}$` +
      value.substring(end);

    onChange(newText);

    const newPosition = start + latex.length + 2;
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newPosition, newPosition);
    }, 0);

    closeMathEditor();
  };

  return (
    <div className="inline-math-field relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#32289a] ${textareaClassName}`}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
      />

      {showButton && !disabled && (
        <div className="flex justify-end mt-1">
          <button
            onClick={openMathEditor}
            className={`text-xs text-gray-400 flex items-center ${buttonClassName}`}
            title={buttonTitle}
          >
            <span className="mr-1 text-xs font-serif italic">S</span>
            {buttonLabel}
          </button>
        </div>
      )}

      <InlineMathModal
        isOpen={showMathEditor}
        initialLatex={initialLatex}
        anchorRect={anchorRect}
        onInsert={handleInsertMath}
        onClose={closeMathEditor}
      />
    </div>
  );
};

export default InlineMathField;
